import * as Types from "../shared/Types";
import {
  BATCH_TYPE_FLOUR_WT,
  LEVAIN_PCT_TYPE_PFF,
  LEVAIN_PCT_TYPE_TOTAL_WT,
  noPreferment,
  PREFERMENT_BUFFER_MIN_WEIGHT,
  PREFERMENT_BUFFER_PCT_FRACTION,
  SUBMIX_TYPE_PORRIDGE, SUBMIX_TYPE_SCALD,
  SUBMIX_TYPE_SOAKER
} from "../shared/Types";
import {unitGrams, unitTeaspoon} from "../shared/Units";
import {toFixedDecimal, toFixedWholePercent} from "../utils/NumberUtils";
import {getYeastPercentFromId, yeastDefs} from "../shared/Yeast";
import {getLevainTime, getYeastTime} from "../shared/FermentationTiming";
import {getSumForField} from "../utils/ArrayUtils";
import {
  calculateLevainFlourByTotalWeight,
  calculateYeastForRatio, getPrefermentComponents,
  getPrefermentRefreshBuildComponents, getWeightDistribution,
  getAllIngredientSummary,
  levainPffToTw,
  levainTwToPff, calculateIngredientCosts
} from "./CalculatorUtils";
import {
  getLiquidContributingInclusionWeight
} from "../shared/IngredientSupport";

const ratioRecipeItems = {
  formName: '',
  formType: '',
  attribution: '',
  createdAt: '',
  modifiedAt: '',
  notes: '',
  numBatches: 1,
  batchSize: 0,
  batchType: Types.BATCH_TYPE_TOTAL_WT,
  includePreferment: false,
  // pffBakersPct is a shorthand for: preferment included and prefermentPffPctType is PFF
  pffBakersPct: false,
  bakersPercentFlourWeight: 0,
  recipeHydrationPct: 0,
  effectiveRecipeFlourTotalWeight: 0,
  effectiveRecipeLiquidTotalWeight: 0,
  effectiveRecipeHydrationPct: 0,
  recipeFlourTotalWeight: 0,
  recipeLiquidTotalWeight: 0,
  recipeYeastTotalWeight: 0,
  recipeStarterTotalWeight: 0,
  recipeMixinTotalWeight: 0,
  recipeSubmixTotalWeight: 0,
  recipeUnitWeight: 0,
  recipeTotalWeight: 0,
  ingredientSummaries: {},
  costData: [],
  ingredientCosts: [],
  mainDough: {
    hydrationPct: 0,
    hydrationPctFraction: 0,
    process: {},
    prefermentPffPct: 0,
    prefermentPffPctFraction: 0,
    prefermentPffPctType: LEVAIN_PCT_TYPE_PFF,
    prefermentEffPffPct: 0,
    starterType: LEVAIN_PCT_TYPE_TOTAL_WT,
    starterPct: 0,
    starterAltTypePct: 0,
    starterPctFraction: 0,
    starterHydrationPct: 0,
    starterHydrationPctFraction: 0,
    yeastInputRatioId: 0,
    yeastInputPct: 0,
    yeastInputPctFraction: 0,
    yeast: {
      flourUnitGrams: yeastDefs.flourUnitGrams,
      ratioId: 0,
      percent: 0,
      weight: 0,
      altUnit: unitTeaspoon,
      altUnitAmount: 0,
      volumeDisplayLabel: ''
    },
    starterWeight: 0,
    starterAltUnit: unitGrams,
    starterAltUnitAmount: 0,
    flourWeight: 0,
    flourInput: [],
    flour: [],
    liquidWeight: [],
    liquidInput: [],
    liquid: [],
    mixinInput: [],
    mixin: [],
    submixesInput: [],
    submixes: [],
    toppingsInput: [],
    toppings: [],
    totalWeight: 0,
    prefermentByWeightPct: 0
  },
  prefermentDough: {
    prefermentType: noPreferment,
    hydrationPct: 0,
    hydrationPctFraction: 0,
    levainFermPct: 0,
    levainFermTempF: '0',
    levainFermTimeStr: '',
    starterType: LEVAIN_PCT_TYPE_TOTAL_WT,
    starterPct: 0,
    starterPctFraction: 0,
    starterHydrationPct: 0,
    starterHydrationPctFraction: 0,
    yeastInputRatioId: 0,
    yeastInputPct: 0,
    yeastInputPctFraction: 0,
    yeast: {
      flourUnitGrams: yeastDefs.flourUnitGrams,
      ratioId: 0,
      percent: 0,
      weight: 0,
      altUnit: unitTeaspoon,
      altUnitAmount: 0,
      volumeDisplayLabel: ''
    },
    starterWeight: 0,
    starterAltUnit: unitGrams,
    starterAltUnitAmount: 0,
    addBuffer: false,
    flourWeight: 0,
    flourEffectiveWeight: 0,
    flourInput: [],
    flour: [],
    liquidWeight: [],
    liquidEffectiveWeight: [],
    liquidInput: [],
    liquid: [],
    mixinInput: [],
    mixin: [],
    totalWeight: 0,
    totalBufferedWeight: 0,
    refresh1: {
      starterWeight: 0,
      flourWeight: 0,
      liquidWeight: 0,
      totalWeight: 0
    },
    refresh2: {
      starterWeight: 0,
      flourWeight: 0,
      liquidWeight: 0,
      totalWeight: 0
    }
  }
}


function calculateDoughRecipe(inputData) {
  let items = JSON.parse(JSON.stringify(ratioRecipeItems))
  items.formName = inputData.formName
  items.formType = inputData.formType
  items.attribution = inputData.attribution
  items.createdAt = inputData.createdAt
  items.modifiedAt = inputData.modifiedAt
  items.notes = inputData.notes
  items.numBatches = inputData.numBatches
  items.batchSize = inputData.batchSize
  items.batchType = inputData.batchType
  items.includePreferment = inputData.includePreferment
  items.pffBakersPct = inputData.pffBakersPct
  items.recipeHydrationPct = inputData.hydrationPct
  items.costData = inputData.costData
  items.mainDough.prefermentPffPct = inputData.prefermentPffPct
  items.mainDough.prefermentPffPctFraction = inputData.prefermentPffPct / 100
  items.mainDough.prefermentPffPctType = inputData.prefermentPffPctType
  items.mainDough.hydrationPct = inputData.hydrationPct
  items.mainDough.hydrationPctFraction = inputData.hydrationPct / 100
  items.mainDough.starterType = inputData.starterType
  items.mainDough.starterPct = inputData.starterPct
  items.mainDough.starterPctFraction = inputData.starterPct / 100
  items.mainDough.starterHydrationPct = inputData.starterHydrationPct
  items.mainDough.starterHydrationPctFraction = inputData.starterHydrationPct / 100
  items.mainDough.yeastInputRatioId = inputData.yeastRatioId
  items.mainDough.yeastInputPct = inputData.yeastPct
  items.mainDough.yeastInputPctFraction = inputData.yeastPct / 100
  items.mainDough.flourInput = inputData.flour
  items.mainDough.liquidInput = inputData.liquid
  items.mainDough.mixinInput = inputData.mixin
  items.mainDough.submixesInput = inputData.submixes
  items.mainDough.toppingsInput = inputData.toppings
  items.mainDough.process = inputData.process

  if (inputData.includePreferment) {
    items.prefermentDough.prefermentType = inputData.prefermentData.prefermentType
    items.prefermentDough.levainFermPct = inputData.prefermentData.levainFermPct
    items.prefermentDough.levainFermTempF = inputData.prefermentData.levainFermTempF
    items.prefermentDough.hydrationPct = inputData.prefermentData.hydrationPct
    items.prefermentDough.hydrationPctFraction = inputData.prefermentData.hydrationPct / 100
    items.prefermentDough.yeastInputRatioId = inputData.prefermentData.yeastRatioId
    items.prefermentDough.yeastInputPct = inputData.prefermentData.yeastPct
    items.prefermentDough.yeastInputPctFraction = inputData.prefermentData.yeastPct / 100
    items.prefermentDough.starterType = inputData.prefermentData.starterType
    items.prefermentDough.starterPct = inputData.prefermentData.starterPct
    items.prefermentDough.starterPctFraction = inputData.prefermentData.starterPct / 100
    items.prefermentDough.starterHydrationPct = inputData.prefermentData.starterHydrationPct
    items.prefermentDough.starterHydrationPctFraction = inputData.prefermentData.starterHydrationPct / 100
    items.prefermentDough.addBuffer = inputData.prefermentData.addBuffer
    items.prefermentDough.flourInput = inputData.prefermentData.flour
    items.prefermentDough.liquidInput = inputData.prefermentData.liquid
    items.prefermentDough.mixinInput = inputData.prefermentData.mixin
  }
  return calculateRecipeWeights(items)
}

// Big picture...
//  The total recipe flour, on which bakers percentages are based, may be determined slightly differently
//  depending on:
//    (A) No Preferment formula and Main uses levain rather than yeast
//    (B) Detailed Preferment formula, Main specifies Preferment as Weight%
//    (C) Detailed Preferment formula, Main specifies Preferment as PFF% (aka pffBakersPct)
//
//  (A) No Preferment formula and Main specifies levain rather than yeast
//    Simplest, most straightforward formula where the levain build details are not of concern and it is just treated
//    as its own ingredient.
//    Though simple, it is potentially the least accurate in terms of the calculated hydration percent,
//    especially as you scale up the batch sizing.
//    The Bakers% basis in this scenario includes only the Main dough flour weight and ignores flour and liquid used to create the levain.
//    The liquid calculated for the recipe is based on the Main dough flour to meet the specified hydro%.
//    Many formulas use a levain built with equal parts water and flour, but when a stiffer or more liquid levain is used,
//    hydration % becomes even less accurate.
//    While recipe rendering has the standard calculation of hydro%, it also reveals an "effective hydro%" taking the
//    levain (preferment) implied flour and water (assuming equal parts) and produces a different hydro%.
//    This can result in a different hydro% the baker may or may not have considered... and can be adjusted or ignored.
//  (B) Preferment Formula as Weight%
//    In this scenario, even though a detailed preferment formula is provided, the Bakers % basis is still the Main dough flour total weight.
//    i.e. exactly the same as (A) - no Preferment.
//    This serves many bakers' expectations of treating a preferment (even with a detailed formula) as a separate ingredient.
//    Similar to (A), the recipe rendering informs both the standard hydration and the effective calculations, this time
//    effective hydration takes into account the actual levain build details (flour and liquid ratio)
//  (C) Preferment Formula as PFF% (aka pffBakersPct)
//    By choosing preferment as PFF%, the total flour being used in the overall formula is recognized, including the flour
//    used to build the preferment.
//    Note that the preferment's starter flour and liquid are also added to the "preferment flour" and likewise for liquid.
//    The reasoning for this is to ensure accurate and stable portioning of the preferment's weight as a whole.
//    For example, if this method was not applied, changes to the preferment starter seed percentage will bleed into the
//    preferment weight (bloating or shrinking). That can be confusing to see the main dough flour and liquid amounts
//    changing as a result.  Furthermore, the main dough will incorporate a larger or smaller than planned preferment,
//    potentially affecting the main dough fermentation speed.
//    A drawback of this is that the bakers percent flour basis now becomes different (a bit larger) than the recipe flour totals
//    shown in the recipe, i.e. the new flour. This will be called out the recipe rendering as a footnote.
//    The benefit of Preferment Formula as PFF% is a more accurate overall hydro%.
//    Note there usually will not be a different "effective hydro" value shown in the recipe rendering in this scenario.
//
function calculateRecipeWeights(items) {
  let flourAndLiquidWeights = calculateRecipeLiquidAndFlour(items)

  // When calculating weights for all main dough ingredients (other than levain/starter), the effectiveRecipeFlourWeight
  // must be used as the basis to get everything in terms of Bakers Percentages.
  // This includes main dough flour, water, mixins, soakers, yeast and main levain weight.
  let recipeFlourWeight = flourAndLiquidWeights.flourWeight
  let recipeLiquidWeight = flourAndLiquidWeights.liquidWeight
  // consoleLog(`flourAndLiquidWeights: recipeFlourWeight=${recipeFlourWeight}, recipeLiquidWeight=${recipeLiquidWeight}`)

  let primaryWeights = calculateAllComponentWeights(recipeFlourWeight, items)
  // consoleLog(`component weights: ${JSON.stringify(primaryWeights)}`)
  // main weights
  let mainLevainWeight = primaryWeights.mainDough.levainWeight
  let mainEffLevainFlourWeight = primaryWeights.mainDough.effectiveLevainFlourWeight
  let mainEffLevainLiquidWeight = primaryWeights.mainDough.effectiveLevainLiquidWeight

  // preferment weights
  let prefermentDoughFlourWeight = primaryWeights.prefermentDough.doughFlourWeight
  let prefermentDoughLiquidWeight = primaryWeights.prefermentDough.doughLiquidWeight
  let prefermentLevainWeight = primaryWeights.prefermentDough.levainWeight
  let prefermentEffLevainFlourWeight = primaryWeights.prefermentDough.effectiveLevainFlourWeight
  let prefermentEffLevainLiquidWeight = primaryWeights.prefermentDough.effectiveLevainLiquidWeight

  // recalculate mainDough flour based on ratios for each flour
  // Note: a point of confusion here is why we are applying main dough percentages to the total recipe flour rather
  // than the primaryWeights.mainDough.doughFlourWeight?
  // It so happens we don't use the primaryWeights.mainDough.doughFlourWeight, but instead use the overall
  // recipe flour total as the basis to handle a variety of scenarios...
  //   (1) in the Preferment PFF scenario, we inject the PFF Preferment portions into the UI to visually recognize
  //   the preferment's portion, making the total recipe flour basis practical
  //   (2) in the Preferment Weight scenario, we exclude the preferment in the initial flour calculation.  The preferment
  //   gets "added back" by taking the preferment weight percentage times the total recipe flour which allows us to
  //   apportion the makeup of the Preferment as its own ingredient.
  //   (3) in the No Preferment (levain as weight%), we handle it similarly to #2
  let mainDoughFlourWeightAdjusted = applyFlourDistribution(items.mainDough, recipeFlourWeight)
  // recalculate mainDough liquids based on ratios for each liquid
  let mainDoughLiquidWeightAdjusted = applyLiquidDistribution(items.mainDough, recipeFlourWeight)

  // supplemental mixes (submixes)... flour and liquid are recognized only from scald mixes
  const recipePorridgeDist = applyPorridgeDistribution(items.mainDough, recipeFlourWeight)
  const recipeScaldDist = applyScaldDistribution(items.mainDough, recipeFlourWeight)
  const recipeSoakerDist = applySoakerDistribution(items.mainDough, recipeFlourWeight)
  items.recipeSubmixTotalWeight = recipePorridgeDist.totalWeight + recipeScaldDist.totalWeight + recipeSoakerDist.totalWeight

  // recalculate prefermentDough flour and liquid ingredients based on desired percentages...
  // preferment flour %'s based on visible flour total, i.e. excluding implicit levain flour since overall weight will include starter
  let prefermentDoughFlourWeightAdjusted = applyFlourDistribution(items.prefermentDough, prefermentDoughFlourWeight)
  // preferment liquid %'s based on visible liquid total, i.e. excluding implicit levain liquid since overall weight will include starter
  let prefermentDoughLiquidWeightAdjusted = applyLiquidDistribution(items.prefermentDough, prefermentDoughLiquidWeight)

  // effective flour weight includes implied flour in preferment by weight or main starter when treated as ordinary ingredients
  let effectiveRecipeFlourWeight = mainDoughFlourWeightAdjusted + prefermentDoughFlourWeightAdjusted + mainEffLevainFlourWeight
          + prefermentEffLevainFlourWeight + recipeScaldDist.flourWeight + recipePorridgeDist.flourWeight
  let effectiveRecipeLiquidWeight = mainDoughLiquidWeightAdjusted + prefermentDoughLiquidWeightAdjusted + mainEffLevainLiquidWeight
          + prefermentEffLevainLiquidWeight + recipeScaldDist.liquidWeight + recipePorridgeDist.liquidWeight
  // Note: exclude the main levain eff flour from bakersPercentFlourWeight (when using pff) because the main:preferment ratios
  // were based on total flour without any implied main levain flour in the initial flour/liquid calculations.
  // However, we want to keep the true effective recipe flour and liquid for revealing actual eff hydration.
  // Without this exclusion, recipe rendering percentages are skewed a bit.
  const bakersPercentFlourWeight = items.pffBakersPct ? (effectiveRecipeFlourWeight - mainEffLevainFlourWeight) : mainDoughFlourWeightAdjusted

  // consoleLog(`recipeFlourWeight=${recipeFlourWeight}, recipeLiquidWeight=${recipeLiquidWeight}, effectiveRecipeFlourWeight=${effectiveRecipeFlourWeight}, effectiveRecipeLiquidWeight=${effectiveRecipeLiquidWeight}`)

  // adjust main dough liquid if needed...
  items.mainDough.liquid.sort((a, b) => b.weight - a.weight)  // weight descending
  // adjust recipe liquid to account for starter inferred liquid
  if (items.pffBakersPct) {
    let recipeLiquidCurrent = effectiveRecipeLiquidWeight
    // target needs to include inferred main levain liquid which wasn't present in the initial flour/liquid calculation
    let mainLiquidTarget = recipeLiquidWeight + mainEffLevainLiquidWeight
    if (recipeLiquidCurrent !== mainLiquidTarget) {
      let liquidImbalance = recipeLiquidCurrent - mainLiquidTarget
      let highestWeightLiquid = items.mainDough.liquid[0]
      highestWeightLiquid['weight'] -= liquidImbalance
      // consoleLog(`calculator adjusting effectiveRecipeLiquidWeight from ${effectiveRecipeLiquidWeight} to ${effectiveRecipeLiquidWeight - liquidImbalance} (delta: ${- liquidImbalance})`)
      // consoleLog(`calculator adjusting mainDoughLiquid from ${mainDoughLiquidWeightAdjusted} to ${mainDoughLiquidWeightAdjusted - liquidImbalance} (delta: ${- liquidImbalance})`)
      mainDoughLiquidWeightAdjusted -= liquidImbalance
      effectiveRecipeLiquidWeight -= liquidImbalance
    }
  }
  

  items.mainDough.liquidWeight = mainDoughLiquidWeightAdjusted
  items.prefermentDough.liquidWeight = prefermentDoughLiquidWeightAdjusted
  items.prefermentDough.liquidEffectiveWeight = prefermentDoughLiquidWeightAdjusted + prefermentEffLevainLiquidWeight

  // main inclusions
  let mainMixinWeight = applyMixinDistribution(items.mainDough, bakersPercentFlourWeight)

  // topping items are just a category of mixins, but weight doesn't count in dough weight
  items.mainDough.toppingsInput.forEach((td, ix) => {
    items.mainDough.toppings.push({id: td.id, name: td.name, group: 'Toppings', percent: td.percent, weight: 0})
  })

  // prefermentDough weights...
  items.prefermentDough.starterWeight = prefermentLevainWeight

  items.prefermentDough.flourWeight = prefermentDoughFlourWeightAdjusted
  items.prefermentDough.flourEffectiveWeight = prefermentDoughFlourWeightAdjusted + prefermentEffLevainFlourWeight
  items.prefermentDough.yeast = calculateYeastForRatio(prefermentDoughFlourWeightAdjusted, items.prefermentDough.yeastInputRatioId, items.prefermentDough.yeastInputPct)
  // consoleLog(`prefermentDough yeast: ${JSON.stringify(items.prefermentDough.yeast)}`)

  // preferment inclusions
  let prefermentMixinWeight = applyMixinDistribution(items.prefermentDough, items.prefermentDough.flourEffectiveWeight)
  items.prefermentDough.totalWeight = Math.round(prefermentDoughFlourWeightAdjusted + prefermentDoughLiquidWeightAdjusted + prefermentLevainWeight + items.prefermentDough.yeast.weight + prefermentMixinWeight)

  // mainDough weights...
  items.mainDough.starterWeight = mainLevainWeight
  items.mainDough.starterFlourWeight = mainEffLevainFlourWeight
  items.mainDough.starterLiquidWeight = mainEffLevainLiquidWeight
  items.mainDough.starterFlourPercent = Math.round(items.mainDough.starterPct * (mainEffLevainFlourWeight / mainLevainWeight))
  items.mainDough.starterLiquidPercent = Math.round(items.mainDough.starterPct * (mainEffLevainLiquidWeight / mainLevainWeight))

  items.mainDough.flourWeight = mainDoughFlourWeightAdjusted
  if (items.mainDough.starterType === LEVAIN_PCT_TYPE_TOTAL_WT) {
    items.mainDough.starterAltTypePct = Math.round(100 * levainTwToPff(bakersPercentFlourWeight, items.mainDough.starterPctFraction, items.mainDough.starterHydrationPctFraction))
  } else {
    items.mainDough.starterAltTypePct = levainPffToTw(bakersPercentFlourWeight, items.mainDough.starterWeight)
  }
  items.mainDough.yeast = calculateYeastForRatio(bakersPercentFlourWeight, items.mainDough.yeastInputRatioId, items.mainDough.yeastInputPct)
  // consoleLog(`mainDough yeast: ${JSON.stringify(items.mainDough.yeast)}`)
  items.mainDough.totalWeight = Math.round(mainDoughFlourWeightAdjusted + mainDoughLiquidWeightAdjusted + mainLevainWeight + mainMixinWeight + items.recipeSubmixTotalWeight + items.mainDough.yeast.weight)
  items.mainDough.prefermentByWeightPct = toFixedWholePercent(items.prefermentDough.totalWeight / bakersPercentFlourWeight, 2)

  // timing projections...
  if (items.prefermentDough.flourWeight > 0) {
    // only sending preferment timing to the recipe rendering
    if (items.prefermentDough.starterPct > 0) {
      items.prefermentDough.levainFermTimeStr = getLevainTime(items.prefermentDough.levainFermTempF, items.prefermentDough.levainFermPct, items.prefermentDough.starterPct, items.prefermentDough.hydrationPct)
    } else if (items.prefermentDough.yeastInputRatioId > 0) {
      items.prefermentDough.levainFermTimeStr = getYeastTime(items.prefermentDough.levainFermTempF, items.prefermentDough.levainFermPct, items.prefermentDough.yeastInputRatioId, items.prefermentDough.hydrationPct)
    }
  }

  // finalize recipe totals...
  items.recipeFlourTotalWeight = Math.round(mainDoughFlourWeightAdjusted + prefermentDoughFlourWeightAdjusted + recipeScaldDist.flourWeight + recipePorridgeDist.flourWeight)
  items.recipeLiquidTotalWeight = mainDoughLiquidWeightAdjusted + prefermentDoughLiquidWeightAdjusted + recipeScaldDist.liquidWeight + recipePorridgeDist.liquidWeight
  items.effectiveRecipeFlourTotalWeight = effectiveRecipeFlourWeight
  items.recipeMixinTotalWeight = mainMixinWeight + prefermentMixinWeight

  items.recipeYeastTotalWeight = toFixedDecimal(items.mainDough.yeast.weight + items.prefermentDough.yeast.weight, 1)
  items.recipeStarterTotalWeight = mainLevainWeight + prefermentLevainWeight

  items.recipeTotalWeight = items.mainDough.totalWeight + items.prefermentDough.totalWeight
  items.recipeUnitWeight = Math.round(items.recipeTotalWeight / items.numBatches)

  // items.recipeHydrationPct = items.pffBakersPct ? items.effectiveRecipeHydrationPct : toFixedDecimal(100 * (mainDoughLiquidWeightAdjusted / mainDoughFlourWeightAdjusted))

  // calculate effective hydro % to include liquid contributing ingredients
  const allIngredientSummaries = getAllIngredientSummary(items)
  items.ingredientSummaries = allIngredientSummaries

  // consoleLog(`calcIngredientCosts:: ingredientSummaries: ${JSON.stringify(items.ingredientSummaries)}`)
  // consoleLog(`calcIngredientCosts:: costData: ${JSON.stringify(items.costData)}`)
  items.ingredientCosts = calculateIngredientCosts(allIngredientSummaries, items.costData, items.numBatches)
  // consoleLog(`calcIngredientCosts:: ingredientCosts: ${JSON.stringify(items.ingredientCosts)}`)

  let liquidContributingIngredientWeight = getLiquidContributingInclusionWeight(allIngredientSummaries.inclusion)
  // consoleLog(`sum total liquid contributing weight: ${liquidContributingIngredientWeight}`)
  items.effectiveRecipeLiquidTotalWeight = effectiveRecipeLiquidWeight + liquidContributingIngredientWeight
  items.effectiveRecipeHydrationPct = toFixedDecimal(100 * (items.effectiveRecipeLiquidTotalWeight / effectiveRecipeFlourWeight))


  items.bakersPercentFlourWeight = bakersPercentFlourWeight

  // adjust preferment formula for buffered weight if requested
  // note that the real, unbuffered preferment weight has already been applied (above) to calculate the main dough
  let formulaTargetPrefermentWeight = items.prefermentDough.totalWeight
  if (items.includePreferment && items.prefermentDough.addBuffer) {
    let bufferWeight = Math.max(Math.round(items.prefermentDough.totalWeight * PREFERMENT_BUFFER_PCT_FRACTION), PREFERMENT_BUFFER_MIN_WEIGHT)
    formulaTargetPrefermentWeight += bufferWeight
    const bufferedEffPrefermentFlourWeight = calculateLevainFlourByTotalWeight(formulaTargetPrefermentWeight, items.prefermentDough.yeastInputPctFraction, items.prefermentDough.hydrationPctFraction, items.prefermentDough.mixinInput)
    const bufferedComp = getPrefermentComponents(bufferedEffPrefermentFlourWeight, items.prefermentDough.starterPctFraction, items.prefermentDough.starterHydrationPctFraction, items.prefermentDough.hydrationPctFraction)
    // consoleLog(`Buffered preferment: ${JSON.stringify(bufferedComp)}`)

    const bufferedPrefermentFlourWeight = bufferedComp.newFlourWt
    const bufferedPrefermentLiquidWeight = bufferedComp.newLiquidWt
    items.prefermentDough.starterWeight = Math.round(bufferedComp.seedWeight)
    items.prefermentDough.yeast = calculateYeastForRatio(bufferedPrefermentFlourWeight, items.prefermentDough.yeastInputRatioId, items.prefermentDough.yeastInputPct)
    // recalculate preferment buffered flour distribution based on new flour weight
    prefermentDoughFlourWeightAdjusted = applyFlourDistribution(items.prefermentDough, bufferedPrefermentFlourWeight)
    items.prefermentDough.flourWeight = prefermentDoughFlourWeightAdjusted
    // recalculate preferment buffered liquid distribution based on new liquid weight
    prefermentDoughLiquidWeightAdjusted = applyLiquidDistribution(items.prefermentDough, bufferedPrefermentLiquidWeight)
    items.prefermentDough.liquidWeight = prefermentDoughLiquidWeightAdjusted
    // recalculate preferment buffered inclusions based on effective flour weight
    let prefermentDoughMixinWeightAdjusted = applyMixinDistribution(items.prefermentDough, bufferedEffPrefermentFlourWeight)
    // Note: buffered weight uses a different field...
    //  Don't change items.prefermentDough.totalWeight, needed for recipe rendering of actual desired preferment weight
    items.prefermentDough.totalBufferedWeight = Math.round(prefermentDoughFlourWeightAdjusted + prefermentDoughLiquidWeightAdjusted + prefermentDoughMixinWeightAdjusted + items.prefermentDough.starterWeight + items.prefermentDough.yeast.weight)
    // update the target weight to allow for rounding
    formulaTargetPrefermentWeight = items.prefermentDough.totalBufferedWeight
    // update the effective flour weight visible in recipe rendering
    items.prefermentDough.flourEffectiveWeight = bufferedEffPrefermentFlourWeight
  }

  // generate levain two-step refresh build sequence: first at original seed%, second at 100% seed
  //  (only applicable for levains that are just flour, water and starter - exclude levains with inclusions)
  if (items.includePreferment && items.prefermentDough.starterWeight > 0 && items.prefermentDough.mixinInput.length === 0) {
    const refreshComp = getPrefermentRefreshBuildComponents(formulaTargetPrefermentWeight, 0, items.prefermentDough.starterPctFraction,
            items.prefermentDough.starterHydrationPctFraction, items.prefermentDough.hydrationPctFraction, [])
    items.prefermentDough.refresh1 = refreshComp.refresh1
    items.prefermentDough.refresh2 = refreshComp.refresh2
  }

  // log the summarized relevant percentages
  // consoleLog(`--SUMMARY--`)
  // consoleLog(`pffBakersPct: ${items.pffBakersPct}`)
  // consoleLog(`mainDoughFlourWeightAdjusted: ${mainDoughFlourWeightAdjusted}, mainEffLevainFlourWeight: ${mainEffLevainFlourWeight}, totalMainFlour: ${mainDoughFlourWeightAdjusted + mainEffLevainFlourWeight}`)
  // consoleLog(`prefermentDoughFlourWeightAdjusted: ${prefermentDoughFlourWeightAdjusted}, prefermentEffLevainFlourWeight: ${prefermentEffLevainFlourWeight}, totalPrefermentFlour: ${prefermentDoughFlourWeightAdjusted + prefermentEffLevainFlourWeight}`)
  // consoleLog(`mainDoughLiquidWeightAdjusted: ${mainDoughLiquidWeightAdjusted}, mainEffLevainLiquidWeight: ${mainEffLevainLiquidWeight}, totalMainLiquid: ${mainDoughLiquidWeightAdjusted + mainEffLevainLiquidWeight}`)
  // consoleLog(`prefermentDoughLiquidWeightAdjusted: ${prefermentDoughLiquidWeightAdjusted}, prefermentEffLevainLiquidWeight: ${prefermentEffLevainLiquidWeight}, totalPrefermentLiquid: ${prefermentDoughLiquidWeightAdjusted + prefermentEffLevainLiquidWeight}`)
  // consoleLog(`effectiveRecipeFlourWeight: ${effectiveRecipeFlourWeight}, effectiveRecipeLiquidWeight: ${effectiveRecipeLiquidWeight}`)
  // consoleLog(`bakersPercentFlourWeight: ${bakersPercentFlourWeight}`)
  // consoleLog(`eff preferment %PFF: ${items.mainDough.prefermentEffPffPct}%`)
  // consoleLog(`preferment %Weight: ${toFixedDecimal(100 * items.prefermentDough.totalWeight / bakersPercentFlourWeight)}`)
  // consoleLog(`submixes: ${JSON.stringify(items.mainDough.submixes)}`)
  // consoleLog(`recipe    hydration percent: ${items.recipeHydrationPct}`)
  // consoleLog(`effective hydration percent: ${items.effectiveRecipeHydrationPct}`)
  return items;
}

// recalculate and apply dough flour based on ratios for each flour
// note: this overwrites any existing flour array
function applyFlourDistribution(dough, doughFlourWeight) {
  let doughFlourWeightAdjusted = 0
  dough.flour = []
  dough.flourInput.forEach((fd, ix) => {
    if (!fd.ro) {
      let flourWeight = Math.round(doughFlourWeight * (fd.percent / 100))
      dough.flour.push({id: fd.id, name: fd.name, group: 'Flour', percent: fd.percent, weight: flourWeight})
      doughFlourWeightAdjusted += flourWeight
    }
  })
  return doughFlourWeightAdjusted
}

// recalculate and apply dough liquids based on ratios for each liquid
// note: this overwrites any existing liquid array
function applyLiquidDistribution(dough, doughLiquidWeight) {
  let doughLiquidWeightAdjusted = 0
  dough.liquid = []
  dough.liquidInput.forEach((ld, ix) => {
    if (!ld.ro) {
      let liquidWeight = Math.round(doughLiquidWeight * (ld.percent / 100))
      dough.liquid.push({id: ld.id, name: ld.name, group: 'Liquid', percent: ld.percent, weight: liquidWeight})
      doughLiquidWeightAdjusted += liquidWeight
    }
  })
  return doughLiquidWeightAdjusted
}

// recalculate and apply dough mixins based on ratios for each mixin applied to the dough flour
// note: this overwrites any existing mixin array
function applyMixinDistribution(dough, doughFlourWeight) {
  let mixinWeightAdjusted = 0
  dough.mixin = []
  dough.mixinInput.forEach((md, ix) => {
    let mixinWeight = Math.round(doughFlourWeight * (md.percent / 100))
    dough.mixin.push({id: md.id, name: md.name, percent: md.percent, weight: mixinWeight})
    mixinWeightAdjusted += mixinWeight
  })
  return mixinWeightAdjusted
}


// calculate weights for each porridge ingredient
// Note: for Porridge, NO flour/grain or liquid is included in the overall hydro and flour calculations
function applyPorridgeDistribution(mainDough, recipeFlourWeight) {
  let totalWeight = 0
  let flourWeight = 0
  let liquidWeight = 0
  mainDough.submixesInput?.filter((sm) => sm.type === SUBMIX_TYPE_PORRIDGE).forEach((sm, ix) => {
    const submixTotalWeight = Math.round(recipeFlourWeight * (sm.mainPct / 100))
    const dryPctSum = getSumForField(sm.dry, 'percent', (v) => v / 100)

    // grain ingredients are the basis for the other ingredients' percentages
    const submixGrainWeight = Math.round(submixTotalWeight / (1 + dryPctSum))
    let submixDryGrainWeight = 0
    let submixDryWeight = 0
    const submixGrainIngredients = []

    // flour items of submix should total 100%
    sm.grain?.forEach((smg) => {
      const itemGrainWeight = Math.round(submixGrainWeight * (smg.percent / 100))
      const itemDryGrainWeight = Math.round(itemGrainWeight / (1 + (sm.absorbPct / 100)))
      submixGrainIngredients.push({id: smg.id, name: smg.name, percent: smg.percent, weight: itemDryGrainWeight})
      submixDryGrainWeight += itemDryGrainWeight
    })

    const submixDryIngredients = []
    sm.dry?.forEach((smi) => {
      const itemDryWeight = Math.round(submixDryGrainWeight * (smi.percent / 100))
      submixDryIngredients.push({id: smi.id, name: smi.name, percent: smi.percent, weight: itemDryWeight})
      submixDryWeight += itemDryWeight
    })

    const submixLiquidIngredients = []
    sm.liquid.forEach((sml) => {
      const itemLiquidWeight = Math.round(submixDryGrainWeight * (sml.percent / 100))
      submixLiquidIngredients.push({id: sml.id, name: sml.name, percent: sml.percent, weight: itemLiquidWeight})
    })

    mainDough.submixes.push({id: sm.id, type: sm.type, name: sm.name, mainPct: sm.mainPct, absorbPct: sm.absorbPct,
      notes: sm.notes, weight: submixTotalWeight, grain: submixGrainIngredients,
      liquid: submixLiquidIngredients, dry: submixDryIngredients})
    totalWeight += submixTotalWeight
  })
  return {
    totalWeight: totalWeight,
    flourWeight: flourWeight,
    liquidWeight: liquidWeight
  }
}

// calculate weights for each scald ingredient
// Note: for Scald/Tangzhong, both the flour and liquid are included in the overall hydro and flour calculations
function applyScaldDistribution(mainDough, recipeFlourWeight) {
  let totalWeight = 0
  let flourWeight = 0
  let liquidWeight = 0
  mainDough.submixesInput?.filter((sm) => sm.type === SUBMIX_TYPE_SCALD).forEach((sm, ix) => {
    const submixTotalWeight = Math.round(recipeFlourWeight * (sm.mainPct / 100))
    const liquidPctSum = getSumForField(sm.liquid, 'percent', (v) => v / 100)
    // grain ingredients are the basis for the other ingredients' percentages
    const submixFlourWeight = (submixTotalWeight / (1 + liquidPctSum))

    const submixFlourIngredients = []
    // flour items of submix should total 100%
    sm.flour?.forEach((smf) => {
      const itemFlourWeight = Math.round(submixFlourWeight * (smf.percent / 100))
      submixFlourIngredients.push({id: smf.id, name: smf.name, percent: smf.percent, weight: itemFlourWeight})
      flourWeight += itemFlourWeight
    })

    const submixLiquidIngredients = []
    sm.liquid.forEach((sml) => {
      const itemLiquidWeight = Math.round(submixFlourWeight * (sml.percent / 100))
      submixLiquidIngredients.push({id: sml.id, name: sml.name, percent: sml.percent, weight: itemLiquidWeight})
      // liquid items of submix can be > 100% hydro so add these individually
      liquidWeight += itemLiquidWeight
    })
    mainDough.submixes.push({id: sm.id, type: sm.type, name: sm.name, mainPct: sm.mainPct,
      notes: sm.notes, weight: submixTotalWeight, flour: submixFlourIngredients, liquid: submixLiquidIngredients})
    totalWeight += submixTotalWeight
  })
  return {
    totalWeight: totalWeight,
    flourWeight: flourWeight,
    liquidWeight: liquidWeight
  }
}

// calculate weights for each soaker ingredient
// Note: soaker water is NOT included in the overall hydro calculations
function applySoakerDistribution(mainDough, recipeFlourWeight) {
  let totalWeight = 0
  mainDough.submixesInput?.filter((sm) => sm.type === SUBMIX_TYPE_SOAKER).forEach((sm, ix) => {
    const mainPctFraction = sm.mainPct / 100
    const submixTotalWeight = Math.round(recipeFlourWeight * mainPctFraction)
    const submixDryIngredients = []
    let submixDryWeight = 0
    sm.dry.forEach((smi) => {
      const itemWeight = Math.round(submixTotalWeight * (smi.percent / 100))
      const itemDryWeight = Math.round(itemWeight / (1 + (sm.absorbPct / 100)))
      submixDryIngredients.push({id: smi.id, name: smi.name, percent: smi.percent, weight: itemDryWeight, })
      submixDryWeight += itemDryWeight
    })
    const submixLiquidIngredients = []
    sm.liquid.forEach((sml) => {
      const liquidWeight = Math.round(submixDryWeight * (sml.percent / 100))
      submixLiquidIngredients.push({id: sml.id, name: sml.name, percent: sml.percent, weight: liquidWeight})
    })

    mainDough.submixes.push({id: sm.id, type: sm.type, name: sm.name, mainPct: sm.mainPct, absorbPct: sm.absorbPct,
      notes: sm.notes, weight: submixTotalWeight, dry: submixDryIngredients, liquid: submixLiquidIngredients})
    totalWeight += submixTotalWeight
  })
  return {
    totalWeight: totalWeight,
    flourWeight: 0,
    liquidWeight: 0
  }
}

// Determine the total recipe flour and liquid by factoring out percentages of non-flour and non-liquid items.
// With the (overall) recipeFlourWeight serving as the bakers percentage basis, we can subsequently
// determine the preferment portion, whether as PFF% or Total Weight %
//
// In addition to factoring out the more obvious non-flour and non-liquid items, the calculation also will
// factor out the following:
//   1. main dough levain percentage, when no preferment formula is present, similar to dry yeast
//   2. entire detailed preferment when the main dough preferment percentage is designated as total weight% rather than PFF%
// The reason for both these cases is to meet most bakers' probable expectation when specifying the leavening agent
// by weight percentages. The baker likely wants to think of leavening as its own ingredient, so the bakers percentage
// basis is limited to the flour explicitly identified in the main dough mix.
//
// The recipe rendering in these cases provides an additional "effective hydration" in addition to the "hydration percentage"
// as an FYI data point. The effective hydration for the main dough levain case is found by inferring additional
// flour and liquid from the main dough levain (assuming 100% hydration, i.e. equal parts).
// For the preferment by total weight case, the actual flour and liquids specified in the detailed preferment taken into
// account, along with the preferment starter% (assuming 100% hydration equal parts for that component).
//
// There are additional behaviors to note with regard to the supplemental mixes - see inline comments
//
function calculateRecipeLiquidAndFlour(items) {
  let recipeFlourWeight
  if (items.batchType === BATCH_TYPE_FLOUR_WT) {
    // Batch size designated as flour weight:
    recipeFlourWeight = Math.round(items.numBatches * items.batchSize)
  } else {
    // Batch size designated as a total weight, solve for the recipe flour by dividing total weight
    // by the known, non-flour/water ingredient percentages:
    //   1. Sum of mixin percentages (including inclusions and all submixes)
    //   2. Sum of dry yeast percentages
    //   3. Total hydration percent
    // TWc = rf + rf*(sum(Mi)) + rf*yp/100 + rf*hp
    //  rf = TWc / (1 + sum(Mi) + yp/100 + hp + msp + ptwp)
    // where:
    //    TWc = total weight (constant)
    //     rf = recipe flour
    //     Mi = mixin pct(s), including main and preferment mixins
    //     yp = yeast pct (percent per 100g flour here), including main and preferment yeast
    //     hp = total hydration pct
    //     msp = main starter/levain pct
    //     ptwp = preferment by total weight pct
    //
    let targetTotalWeight = items.numBatches * items.batchSize

    // main mixins, sum(Mi):
    let mainMixinRatioSum = 0
    items.mainDough.mixinInput.forEach((md) => {
      mainMixinRatioSum += (md.percent / 100)
    })

    let prefermentMixinRatioSum = 0
    let pffPrefermentFlourPctFraction = 0
    if (items.pffBakersPct) {
      // Factor out preferment inclusion (mixin) percentages...  (not common)
      // Previously, the mixin %s were based on the preferment _new_ flour, excluding the preferment seed flour,
      // and to match that basis, we needed to reduce the PFF% by the flour portion of the seed like this:
      // const starterDist = getPercentDistribution(items.prefermentDough.starterPctFraction, items.prefermentDough.starterHydrationPctFraction)
      // pffPrefermentFlourPctFraction = items.mainDough.prefermentPffPctFraction - (items.mainDough.prefermentPffPctFraction * starterDist.liquidPctFraction)

      // HOWEVER, this is now changing to use the full PFF% as the basis for preferment mixins because we'd like the
      // mixin amounts to be a fixed percentage and NOT SUBJECT TO CHANGE IF WE CHANGE THE SIZE OF THE STARTER SEED!!
      pffPrefermentFlourPctFraction = items.mainDough.prefermentPffPctFraction
      items.prefermentDough.mixinInput.forEach((md) => {
        prefermentMixinRatioSum += ((md.percent / 100) * pffPrefermentFlourPctFraction)
      })
    }

    // supplemental mixes: treat soakers and porridges like mixins
    items.mainDough.submixesInput?.filter((sm) => sm.type === SUBMIX_TYPE_SOAKER || sm.type === SUBMIX_TYPE_PORRIDGE).forEach((sm) => {
      mainMixinRatioSum += (sm.mainPct / 100)
    })

    if (! items.pffBakersPct) {
      // exclude scald if bakers pct is main dough flour only... otherwise scald flour and water will be factored in later
      items.mainDough.submixesInput?.filter((sm) => sm.type === SUBMIX_TYPE_SCALD).forEach((sm) => {
        mainMixinRatioSum += (sm.mainPct / 100)
      })
    }

    // yeast percentage, only for the main dough, is proportionate to the respective main and preferment specifications
    const mainYeastPct = items.mainDough.yeastInputPctFraction || (getYeastPercentFromId(items.mainDough.yeastInputRatioId) / yeastDefs.flourUnitGrams)
    let prefermentYeastPct = items.prefermentDough.yeastInputPctFraction || (getYeastPercentFromId(items.prefermentDough.yeastInputRatioId) / yeastDefs.flourUnitGrams)
    prefermentYeastPct = items.pffBakersPct ? prefermentYeastPct * pffPrefermentFlourPctFraction : 0
    const totalYeastPct = mainYeastPct + prefermentYeastPct
    const mainStarterPct = items.mainDough.starterPctFraction

    // factor out entire preferment when specified by weight, i.e. its own ingredient
    let mainPrefermentWeightPctFraction = 0
    if (items.includePreferment && items.mainDough.prefermentPffPctType === LEVAIN_PCT_TYPE_TOTAL_WT) {
      mainPrefermentWeightPctFraction = items.mainDough.prefermentPffPctFraction
    }

    // ...and now the target total flour...
    recipeFlourWeight = Math.round(targetTotalWeight / (1 + mainMixinRatioSum + prefermentMixinRatioSum + totalYeastPct + items.mainDough.hydrationPctFraction + mainStarterPct + mainPrefermentWeightPctFraction))
    // consoleLog(`recipeFlourWeight: ${recipeFlourWeight}`)
  }
  let recipeLiquidWeight = Math.round(recipeFlourWeight * items.mainDough.hydrationPctFraction)
  return {flourWeight: recipeFlourWeight, liquidWeight: recipeLiquidWeight}
}

// Derive both the main and preferment (if designated) component weights
// If a preferment is designated, the total recipe flour quantity is split based on the provided percentage.
// The preferment has its own hydration percentage specified separately from the total recipe hydration percentage.
// When a preferment is designated, the main dough liquid will be the remainder from the preferment liquid to satisfy
// the total recipe hydration.  Note that when levain is specified as PFF, the levain hydration factors into the
// hydration percentage of both the preferment and the total hydration.
function calculateAllComponentWeights(recipeFlourWeight, items) {
  let mainDoughPrimaryWeights = undefined
  let prefermentDoughPrimaryWeights = undefined

  mainDoughPrimaryWeights = calculateMainComponentWeights(recipeFlourWeight, items.mainDough, items.includePreferment)

  // pffPctFraction should be zeroed out when a preferment is included; safety net here to force it zero
  const pffPctFraction = items.includePreferment ? items.mainDough.prefermentPffPctFraction : 0
  prefermentDoughPrimaryWeights = calculatePrefermentComponentWeights(recipeFlourWeight, pffPctFraction, items.mainDough.prefermentPffPctType, items.prefermentDough)

  return {
    mainDough: mainDoughPrimaryWeights,
    prefermentDough: prefermentDoughPrimaryWeights
  }
}

function calculateMainComponentWeights(totalFlourWeight, doughParams, hasPreferment) {
  let doughFlourWeight = 0
  let doughLiquidWeight = 0
  let levainWeight = 0
  let effectiveLevainFlourWeight = 0
  let effectiveLevainLiquidWeight = 0

  doughFlourWeight = totalFlourWeight
  doughLiquidWeight = Math.round(totalFlourWeight * doughParams.hydrationPctFraction)

  // Note that levainWeight here will only be present (non-zero) if either...
  //  (a) NOT including a preferment OR (b) including "extra leavening" as starter in the main dough
  levainWeight = Math.round(totalFlourWeight * doughParams.starterPctFraction)
  if (levainWeight > 0) {
    // assume main starter is 100% hydration for now - UI doesn't currently support specifying starter H%
    const levainDistribution = getWeightDistribution(levainWeight, 1)
    effectiveLevainFlourWeight = levainDistribution.flourWeight
    effectiveLevainLiquidWeight = levainDistribution.liquidWeight
  }

  return {
    doughFlourWeight: doughFlourWeight,
    doughLiquidWeight: doughLiquidWeight,
    levainWeight: levainWeight,
    effectiveLevainFlourWeight: effectiveLevainFlourWeight,
    effectiveLevainLiquidWeight: effectiveLevainLiquidWeight
  }
}

function calculatePrefermentComponentWeights(recipeFlourWeight, prefermentPctFraction, prefermentPctType, doughParams) {
  if (prefermentPctFraction === 0) {
    return {
      doughFlourWeight: 0,
      doughLiquidWeight: 0,
      levainWeight: 0,
      effectiveLevainFlourWeight: 0,
      effectiveLevainLiquidWeight: 0
    }
  }

  // Previously, any preferment ingredient (inclusions, if any) percentages were based on the _new_ flour for the preferment.
  // However, this is problematic because the new flour amount varies, depending on the size of the starter seed.
  // This of course is only applicable for levain-type preferments.
  // Going forward, all percentages are based on the effective total flour in the preferment.
  // Note this is consistent with how the overall PFF percentage is calculated to include all effective flour.

  let prefermentEffectiveFlourWeight = 0
  // preferment ingredient %s are based on the total flour weight calculated here...
  if (prefermentPctType === LEVAIN_PCT_TYPE_PFF) {
    // PFF % is based on ALL preferment flour, including starter seed flour, i.e. "effective preferment flour":
    //    PFF % = (eff preferment flour) / (main flour + eff preferment flour)
    // Note: Any "extra" main starter leavening does NOT factor into the PFF calculation.
    // Extra leavening in main is always treated as a separate ingredient, and typically would be yeast anyway
    prefermentEffectiveFlourWeight = Math.round(recipeFlourWeight * prefermentPctFraction)
  } else {  // Preferment as Total Weight ...
    const expectedTotalWeight = Math.round(recipeFlourWeight * prefermentPctFraction)
    prefermentEffectiveFlourWeight = calculateLevainFlourByTotalWeight(expectedTotalWeight, doughParams.yeastInputPctFraction, doughParams.hydrationPctFraction, doughParams.mixinInput)
  }

  const comp = getPrefermentComponents(prefermentEffectiveFlourWeight, doughParams.starterPctFraction, doughParams.starterHydrationPctFraction, doughParams.hydrationPctFraction)

  return {
    doughFlourWeight: comp.newFlourWt,
    doughLiquidWeight: comp.newLiquidWt,
    levainWeight: comp.seedWeight,
    effectiveLevainFlourWeight: comp.seedFlourWt,
    effectiveLevainLiquidWeight: comp.seedLiquidWt
  }
}

function consoleLog(message) {
  // debug
  console.log(message)
}

export {calculateDoughRecipe}